import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type Router,
  type RouteRecordRaw,
} from 'vue-router'
import { i18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Klíč i18n pro název obrazovky (nadpis a title okna). */
    titleKey: string
    /** Obrazovka bez kostry aplikace (přihlášení, registrace, založení firmy). */
    bare?: boolean
    /** Dostupná bez přihlášení; přihlášeného uživatele z ní přesměrujeme pryč. */
    public?: boolean
    /** Veřejná obrazovka pro kohokoli (i přihlášeného): žádné přesměrování na login ani na založení firmy. */
    hosted?: boolean
    /** Jen pro platformní roli wac_admin. */
    admin?: boolean
    /** Přihlášený bez firmy sem smí (pozvánka do firmy), nepřesměruje se na založení firmy. */
    allowWithoutOrg?: boolean
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'today',
    component: () => import('@/views/TodayView.vue'),
    meta: { titleKey: 'nav.today' },
  },
  {
    path: '/zakazky',
    name: 'jobs',
    component: () => import('@/views/JobsView.vue'),
    meta: { titleKey: 'nav.jobs' },
  },
  {
    path: '/zakazky/nova',
    name: 'job-new',
    component: () => import('@/views/JobFormView.vue'),
    meta: { titleKey: 'jobs.new.title' },
  },
  {
    path: '/zakazky/:id',
    name: 'job-detail',
    component: () => import('@/views/JobDetailView.vue'),
    meta: { titleKey: 'jobs.detail.title' },
  },
  {
    path: '/zakazky/:id/upravit',
    name: 'job-edit',
    component: () => import('@/views/JobFormView.vue'),
    meta: { titleKey: 'jobs.edit.title' },
  },
  {
    path: '/klienti',
    name: 'clients',
    component: () => import('@/views/ClientsView.vue'),
    meta: { titleKey: 'nav.clients' },
  },
  {
    path: '/klienti/novy',
    name: 'client-new',
    component: () => import('@/views/ClientFormView.vue'),
    meta: { titleKey: 'clients.new.title' },
  },
  {
    path: '/klienti/import',
    name: 'client-import',
    component: () => import('@/views/ClientImportView.vue'),
    meta: { titleKey: 'clients.import.title' },
  },
  {
    path: '/klienti/:id',
    name: 'client-detail',
    component: () => import('@/views/ClientDetailView.vue'),
    meta: { titleKey: 'clients.detail.title' },
  },
  {
    path: '/klienti/:id/upravit',
    name: 'client-edit',
    component: () => import('@/views/ClientFormView.vue'),
    meta: { titleKey: 'clients.edit.title' },
  },
  {
    path: '/kalendar',
    name: 'calendar',
    component: () => import('@/views/CalendarView.vue'),
    meta: { titleKey: 'nav.calendar' },
  },
  {
    path: '/kalendar/nova',
    name: 'event-new',
    component: () => import('@/views/EventFormView.vue'),
    meta: { titleKey: 'calendar.newEvent' },
  },
  {
    path: '/kalendar/:id',
    name: 'event-edit',
    component: () => import('@/views/EventFormView.vue'),
    meta: { titleKey: 'calendar.editEvent' },
  },
  {
    path: '/sliby',
    name: 'promises',
    component: () => import('@/views/PromisesView.vue'),
    meta: { titleKey: 'nav.promises' },
  },
  {
    path: '/sliby/novy',
    name: 'promise-new',
    component: () => import('@/views/PromiseFormView.vue'),
    meta: { titleKey: 'promises.new.title' },
  },
  {
    path: '/sliby/:id/upravit',
    name: 'promise-edit',
    component: () => import('@/views/PromiseFormView.vue'),
    meta: { titleKey: 'promises.edit.title' },
  },
  {
    path: '/ukoly',
    name: 'tasks',
    component: () => import('@/views/TasksView.vue'),
    meta: { titleKey: 'nav.tasks' },
  },
  {
    path: '/ukoly/novy',
    name: 'task-new',
    component: () => import('@/views/TaskFormView.vue'),
    meta: { titleKey: 'tasks.new.title' },
  },
  {
    path: '/ukoly/:id/upravit',
    name: 'task-edit',
    component: () => import('@/views/TaskFormView.vue'),
    meta: { titleKey: 'tasks.edit.title' },
  },
  {
    path: '/buddy',
    name: 'buddy',
    component: () => import('@/views/BuddyView.vue'),
    meta: { titleKey: 'nav.buddy' },
  },
  {
    path: '/nastaveni',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { titleKey: 'nav.settings' },
  },
  {
    path: '/nastaveni/profil',
    name: 'settings-profile',
    component: () => import('@/views/settings/ProfileSettingsView.vue'),
    meta: { titleKey: 'settings.sections.profile.title' },
  },
  {
    path: '/nastaveni/firma',
    name: 'settings-org',
    component: () => import('@/views/settings/OrgSettingsView.vue'),
    meta: { titleKey: 'settings.sections.org.title' },
  },
  {
    path: '/nastaveni/clenove',
    name: 'settings-members',
    component: () => import('@/views/settings/MembersSettingsView.vue'),
    meta: { titleKey: 'settings.sections.members.title' },
  },
  {
    path: '/nastaveni/notifikace',
    name: 'settings-notifications',
    component: () => import('@/views/settings/NotificationsSettingsView.vue'),
    meta: { titleKey: 'settings.sections.notifications.title' },
  },
  {
    path: '/nastaveni/kalendare',
    name: 'settings-calendars',
    component: () => import('@/views/settings/CalendarsSettingsView.vue'),
    meta: { titleKey: 'settings.sections.calendars.title' },
  },
  {
    path: '/nastaveni/poptavky',
    name: 'settings-leads',
    component: () => import('@/views/settings/LeadsSettingsView.vue'),
    meta: { titleKey: 'settings.sections.leads.title' },
  },
  {
    path: '/nastaveni/buddy',
    name: 'settings-buddy',
    component: () => import('@/views/settings/BuddySettingsView.vue'),
    meta: { titleKey: 'settings.sections.buddy.title' },
  },
  {
    path: '/nastaveni/data',
    name: 'settings-data',
    component: () => import('@/views/settings/DataSettingsView.vue'),
    meta: { titleKey: 'settings.sections.data.title' },
  },
  {
    path: '/nastaveni/vzhled',
    name: 'settings-appearance',
    component: () => import('@/views/settings/AppearanceSettingsView.vue'),
    meta: { titleKey: 'settings.sections.appearance.title' },
  },
  {
    path: '/pozvanka/:id',
    name: 'invitation',
    component: () => import('@/views/InvitationView.vue'),
    meta: { titleKey: 'invitation.title', bare: true, allowWithoutOrg: true },
  },
  {
    path: '/zapomenute-heslo',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { titleKey: 'password.forgot.title', bare: true, public: true },
  },
  {
    path: '/nove-heslo',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { titleKey: 'password.reset.title', bare: true, public: true },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { titleKey: 'admin.title', admin: true },
  },
  {
    path: '/admin/:id',
    name: 'admin-org',
    component: () => import('@/views/AdminOrgView.vue'),
    meta: { titleKey: 'admin.title', admin: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { titleKey: 'login.title', bare: true, public: true },
  },
  {
    path: '/registrace',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { titleKey: 'register.title', bare: true, public: true },
  },
  {
    path: '/zalozeni-firmy',
    name: 'onboarding',
    component: () => import('@/views/OnboardingView.vue'),
    meta: { titleKey: 'onboarding.title', bare: true },
  },
  {
    path: '/zalozeni-firmy/sluzby',
    name: 'onboarding-services',
    component: () => import('@/views/OnboardingServicesView.vue'),
    meta: { titleKey: 'onboarding.services.title', bare: true },
  },
  {
    path: '/zalozeni-firmy/znacka',
    name: 'onboarding-brand',
    component: () => import('@/views/OnboardingBrandView.vue'),
    meta: { titleKey: 'onboarding.brand.title', bare: true },
  },
  {
    path: '/f/:token',
    name: 'lead-form',
    component: () => import('@/views/LeadFormView.vue'),
    meta: { titleKey: 'leadForm.title', bare: true, hosted: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { titleKey: 'notFound.title' },
  },
]

/** Návratová adresa po přihlášení: jen vlastní cesta, nikdy cizí web ani přihlašovací obrazovka. */
export function safeNext(next: unknown): string {
  if (typeof next !== 'string') return '/'
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return '/'
  if (/^\/(login|registrace|zapomenute-heslo|nove-heslo)(\/|\?|$)/.test(next)) return '/'
  return next
}

/**
 * Rozhodnutí guardu. Vrací `true` (pustit) nebo adresu, kam přesměrovat.
 * - hostované obrazovky (/f/:token): vždy pustit, přihlášený i nepřihlášený
 * - nepřihlášený na chráněné URL: /login?next=<cesta>
 * - přihlášený na /login a /registrace: next nebo /
 * - /admin bez role wac_admin: /
 * - přihlášený bez firmy: /zalozeni-firmy (wac_admin bez firmy na /admin); obrazovky s
 *   `allowWithoutOrg` (pozvánka do firmy) projdou
 */
export function guardRoute(
  to: RouteLocationNormalized,
  me: { org: unknown; isAdmin: boolean } | null,
): true | RouteLocationRaw {
  if (to.meta.hosted) return true
  if (!me) {
    if (to.meta.public) return true
    const next = to.fullPath === '/' ? undefined : to.fullPath
    return { path: '/login', query: next ? { next } : {} }
  }
  if (to.meta.public) return safeNext(to.query.next)
  if (to.meta.admin && !me.isAdmin) return '/'
  // Platformní admin bez vlastní firmy smí na /admin, ostatní bez firmy jdou na založení firmy.
  if (
    !me.org &&
    to.name !== 'onboarding' &&
    !to.meta.allowWithoutOrg &&
    !(to.meta.admin && me.isAdmin)
  ) {
    return me.isAdmin ? '/admin' : '/zalozeni-firmy'
  }
  return true
}

/** Při prvním vstupu načte session (jednou), pak každou navigaci pustí přes `guardRoute`. */
export function installGuards(target: Router) {
  target.beforeEach(async (to) => {
    const auth = useAuthStore()
    if (auth.status === 'idle') await auth.load()
    return guardRoute(to, auth.me)
  })
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})
installGuards(router)

router.afterEach((to) => {
  const t = i18n.global.t
  document.title = `${t(to.meta.titleKey)} · ${t('app.name')}`
})
